import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { 
  Box, 
  Container, 
  Typography, 
  Button, 
  Grid, 
  Card, 
  TextField,
  useMediaQuery,
  Paper
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";

const ContactUsPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const fadeInLeft = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };
  
  const fadeInRight = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.8, ease: "easeOut" }
    }
  };

  const contactInfo = [
    {
      icon: <LocationOnIcon sx={{ fontSize: "2rem", color: "#db352a" }} />,
      title: "Our Location",
      details: "Hotel Raviraj, Dr Rajendra Prasad Marg, Adalat Rd, near Baba Petrol Pump, Chhatrapati Sambhaji Nagar, Maharashtra 431005",
      action: {
        text: "Get Directions",
        onClick: () => window.open("https://maps.app.goo.gl/9tyho8AjrbNSRCy7A", "_blank")
      }
    },
    {
      icon: <PhoneIcon sx={{ fontSize: "2rem", color: "#db352a" }} />,
      title: "Phone Numbers",
      details: [
        "+91 9890340816",
        "+91 9890565100", 
        "+91 9922063222"
      ],
      action: {
        text: "Call Us",
        onClick: () => window.open("tel:+919890340816")
      }
    },
    {
      icon: <EmailIcon sx={{ fontSize: "2rem", color: "#db352a" }} />,
      title: "Email Address",
      details: "atkadam88@gmail.com",
      action: {
        text: "Send Email",
        onClick: () => window.open("mailto:atkadam88@gmail.com")
      }
    },
    {
      icon: <AccessTimeIcon sx={{ fontSize: "2rem", color: "#db352a" }} />,
      title: "Office Hours",
      details: "24 x 7 Availability\nWe are here to assist you anytime!",
    }
  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Background pattern */}
      <div style={{ 
        position: "fixed", 
        width: "100%", 
        height: "100vh", 
        zIndex: -1,
        backgroundImage: "url('/images/world-map-background.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        opacity: 0.05
      }} />
      
      <Header />

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: "linear-gradient(to bottom, rgba(219,53,42,0.05) 0%, rgba(255,255,255,1) 100%)",
          position: "relative",
          overflow: "hidden"
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{ 
                  fontWeight: "bold",
                  color: "#db352a",
                  mb: 2,
                  position: "relative",
                  display: "inline-block",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    width: "60%",
                    height: "4px",
                    backgroundColor: "#db352a",
                    bottom: "-12px",
                    left: "20%"
                  }
                }}
              >
                Contact Us
              </Typography>
              
              <Typography 
                variant="h6" 
                sx={{ 
                  maxWidth: "700px", 
                  mx: "auto", 
                  mt: 4,
                  color: "#555"
                }}
              >
                Need assistance planning your trip? Have questions about our services? 
                We're here to help you create memorable travel experiences.
              </Typography>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Contact Information Section */}
      <Container maxWidth="lg" sx={{ my: 8 }}>
        <Grid container spacing={4}>
          {contactInfo.map((info, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: "16px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                    transition: "transform 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-10px)",
                      boxShadow: "0 12px 30px rgba(219,53,42,0.12)"
                    },
                    display: "flex",
                    flexDirection: "column"
                  }}
                >
                  <Box sx={{ textAlign: "center", mb: 2 }}>
                    {info.icon}
                  </Box>
                  
                  <Typography 
                    variant="h6" 
                    align="center" 
                    gutterBottom
                    sx={{ fontWeight: "bold" }}
                  >
                    {info.title}
                  </Typography>
                  
                  <Box sx={{ flexGrow: 1, mb: 3 }}>
                    {Array.isArray(info.details) ? (
                      info.details.map((detail, idx) => (
                        <Typography 
                          key={idx}
                          variant="body2" 
                          align="center" 
                          paragraph
                          sx={{ 
                            color: "#555",
                            whiteSpace: "pre-line"
                          }}
                        >
                          {detail}
                        </Typography>
                      ))
                    ) : (
                      <Typography 
                        variant="body2" 
                        align="center" 
                        paragraph
                        sx={{ 
                          color: "#555",
                          whiteSpace: "pre-line"
                        }}
                      >
                        {info.details}
                      </Typography>
                    )}
                  </Box>
                  
                  {info.action && (
                    <Box sx={{ textAlign: "center", mt: "auto" }}>
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={info.action.onClick}
                          sx={{
                            borderRadius: "30px",
                            borderColor: "#db352a",
                            color: "#db352a",
                            "&:hover": {
                              borderColor: "#db352a",
                              backgroundColor: "rgba(219,53,42,0.05)"
                            }
                          }}
                        >
                          {info.action.text}
                        </Button>
                      </motion.div>
                    </Box>
                  )}
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Map Section */}
      <Box 
        sx={{ 
          py: 8, 
          background: "linear-gradient(to right, rgba(219,53,42,0.05) 0%, rgba(219,53,42,0.1) 100%)"
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInLeft}
              >
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    color: "#db352a",
                    fontWeight: "bold",
                    mb: 3
                  }}
                >
                  Find Us
                </Typography>
                
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    color: "#555",
                    mb: 4
                  }}
                >
                  Located in the heart of Aurangabad, our office is easily accessible. 
                  Visit us to discuss your travel plans in person, or use the map to navigate to our location.
                </Typography>
                
                <Box 
                  sx={{ 
                    p: 3, 
                    borderRadius: "12px", 
                    backgroundColor: "white",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.1)",
                    mb: 4
                  }}
                >
                                      <Typography 
                    variant="body2" 
                    sx={{ 
                      display: "flex",
                      alignItems: "flex-start",
                      color: "#555",
                      mb: 2
                    }}
                  >
                    <LocationOnIcon sx={{ color: "#db352a", mr: 1, mt: 0.5 }} />
                    Hotel Raviraj, Dr Rajendra Prasad Marg, Adalat Rd, near Baba Petrol Pump, Chhatrapati Sambhaji Nagar, Maharashtra 431005
                  </Typography>
                  
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Button
                        variant="contained"
                        color="error"
                        size="medium"
                        onClick={() => window.open("https://maps.app.goo.gl/9tyho8AjrbNSRCy7A", "_blank")}
                        sx={{
                          borderRadius: "30px",
                          px: 3,
                          backgroundColor: "#db352a"
                        }}
                      >
                        Get Directions
                      </Button>
                    </motion.div>
                  </Box>
                </Box>
              </motion.div>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={fadeInRight}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 200 }}
              >
                <Box
  sx={{
    height: "100%",
    width: "100%",
    borderRadius: "10px",
    boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
    overflow: "hidden"
  }}
>
  <iframe
    title="Location Map"
    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3625.169400010629!2d75.3164881!3d19.873905999999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bdb99004e713f8f%3A0x23c552c129301c39!2sAshoka%20Tours%20and%20Travels!5e1!3m2!1sen!2sin!4v1762587953591!5m2!1sen!2sin"
    width="100%"
    height="450"
    style={{ border: 0 }}
    allowFullScreen=""
    loading="lazy"
    referrerPolicy="no-referrer-when-downgrade"
  />
</Box>

              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>


      
      <Footer />
    </Box>
  );
};

export default ContactUsPage;